import {
  FlowOperationType,
  FlowStatus,
  FlowVersion,
  isNil,
  TriggerType,
} from '@activepieces/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';

import { useBuilderStateContext } from './builder-hooks';

import { useSocket } from '@/components/socket-provider';
import {
  INTERNAL_ERROR_TOAST,
  showErrorToast,
  toast,
} from '@/components/ui/use-toast';
import { flowRunsApi } from '@/features/flow-runs/lib/flow-runs-api';
import { useBlockingPiecesStore } from '@/features/flows/components/blocking-pieces-store';
import { getLockedPiecesFromError } from '@/features/flows/lib/flow-error-utils';
import { flowsApi } from '@/features/flows/lib/flows-api';
import { useFlowBlockingPieces } from '@/features/flows/lib/use-flow-blocking-pieces';

/**
 * Publishing and running the whole flow are the builder's own actions: the
 * publish button and the AI chat both call these, never a copy — the publish
 * path checks locked pieces twice (before the call, then on the server's
 * FLOW_CONTAINS_LOCKED_PIECES refusal), and a second copy would drift.
 */
export const useFlowPublish = () => {
  const [flow, flowVersion, setFlow, setVersion] = useBuilderStateContext(
    (state) => [state.flow, state.flowVersion, state.setFlow, state.setVersion],
  );
  const { isBlocked, blockingSteps } = useFlowBlockingPieces(flowVersion);
  const openBlockingPieces = useBlockingPiecesStore(
    (s) => s.openBlockingPieces,
  );
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: async () =>
      flowsApi.update(flow.id, {
        type: FlowOperationType.LOCK_AND_PUBLISH,
        request: {},
      }),
    onSuccess: (published) => {
      toast({
        variant: 'success',
        title: t('Success'),
        description: t('Flow has been published.'),
        duration: 3000,
      });
      setFlow(published);
      setVersion(published.version);
      queryClient.invalidateQueries({ queryKey: ['usage'] });
    },
    onError: (error) => {
      const lockedPieces = getLockedPiecesFromError(error);
      if (lockedPieces) {
        openBlockingPieces({ blockingSteps: lockedPieces, flowId: flow.id });
        return;
      }
      showErrorToast(error);
    },
  });

  const publish = () => {
    if (flow.status !== FlowStatus.ENABLED && isBlocked) {
      openBlockingPieces({ blockingSteps, flowId: flow.id });
      return;
    }
    mutate();
  };

  return {
    publish,
    isPublishing: isPending,
    /** The builder disables its own button on the same rule */
    canPublish: flowVersion.valid,
  };
};

/** A trigger with no sample data has nothing to feed a whole-flow run */
export const triggerHasSampleData = (flowVersion: FlowVersion): boolean =>
  flowVersion.triggers[0]?.type === TriggerType.PIECE &&
  !isNil(flowVersion.triggers[0].settings.inputUiInfo?.lastTestDate);

export const useTestFlowRun = () => {
  const socket = useSocket();
  const [flowVersion, setRun] = useBuilderStateContext((state) => [
    state.flowVersion,
    state.setRun,
  ]);

  const { mutate, isPending } = useMutation<void>({
    mutationFn: () =>
      flowRunsApi.testFlow(socket, { flowVersionId: flowVersion.id }, (run) => {
        setRun(run, flowVersion, false);
      }),
    onError: () => toast(INTERNAL_ERROR_TOAST),
  });

  return {
    testFlow: mutate,
    isTesting: isPending,
    canTestFlow: triggerHasSampleData(flowVersion),
  };
};
